<template>
    <AppLayout title="Write a Review">
        <div class="max-w-[620px] mx-auto pt-4 pb-16">
            <LoginModal
                :modal-visible="loginModalVisible"
                message="Please login or register for free to post this review."
                @success="loginFormSuccess"
                @toggle-modal="toggleLoginModal"
            />

            <div class="pb-5 sm:flex sm:items-center sm:justify-between">
                <div class="flex justify-center w-full">
                    <template v-if="!selectedInvestor">
                        <h1 class="inline-block text-gray-400 text-base font-medium leading-loose">Write a Review</h1>
                    </template>

                    <template v-else>
                        <div class="flex items-center">
                            <h1 class="inline-block text-gray-400 text-base font-medium leading-loose">Write a review
                                for </h1>

                            <div class="text-zinc-800 text-base font-bold leading-loose flex items-center ml-2">
                                <img
                                    :src="selectedInvestor.image" alt="Investor image"
                                    class="w-6 h-6 object-cover rounded-full mr-1">
                                <span>{{ selectedInvestor.name }}</span>
                            </div>
                        </div>
                    </template>
                </div>
            </div>


            <div class="w-full h-[3px] bg-neutral-100 rounded-full overflow-hidden relative">
                <div v-if="currentStep === 1" class="absolute h-[3px] top-0 left-0 w-[50%] bg-zinc-800"/>
                <div v-if="currentStep === 2" class="absolute h-[3px] top-0 left-0 w-[100%] bg-zinc-800"/>

            </div>


            <form ref="form" class="pt-6 space-y-6" @submit.prevent="submit">
                <div class="text-sm text-gray-700 p-4  border  rounded-xl">
                    <div class="flex items-center mb-[10px]">
                        <svg
                            class="mr-1" fill="none" height="24" viewBox="0 0 24 24" width="24"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M20 9.5219C20 14.6273 12.5864 18.7597 12.2707 18.9304C12.1875 18.9761 12.0945 19 12 19C11.9055 19 11.8125 18.9761 11.7293 18.9304C11.4136 18.7597 4 14.6273 4 9.5219C4.00132 8.32303 4.46833 7.17365 5.29856 6.32593C6.12879 5.4782 7.25445 5.00135 8.42857 5C9.90357 5 11.195 5.64765 12 6.74239C12.805 5.64765 14.0964 5 15.5714 5C16.7456 5.00135 17.8712 5.4782 18.7014 6.32593C19.5317 7.17365 19.9987 8.32303 20 9.5219Z"
                                fill="#F65C5C" stroke="#F65C5C" stroke-width="1.5"/>
                        </svg>
                        <div class="text-zinc-800 text-sm font-medium leading-tight">Thanks for contributing to a better
                            venture ecosystem
                        </div>
                    </div>

                    <div v-if="currentStep === 1" class="mb-[10px]">
                        <span class="font-medium">Anonymity:</span> Reviewers may submit their experiences anonymously
                        to protect their identity and
                        prevent any potential backlash.
                    </div>

                    <div v-if="currentStep === 1" class="mb-[10px]">
                        <span class="font-medium">Privacy Protection:</span> We are committed to protecting the
                        privacy of our reviewers. Personal
                        information will not be disclosed, sold, or used for any purpose other than to verify the
                        authenticity of the review.
                    </div>

                    <div v-if="currentStep === 2" class="mb-[10px]">
                        <div class="mb-[10px]">
                            <span class="font-medium">What Reviews Will Be Allowed:</span>
                            <ul class="list-disc pl-4">
                                <li>
                                    Authentic Experiences
                                </li>
                                <li>
                                    Constructive Feedback
                                </li>
                                <li>
                                    Specificity
                                </li>
                                <li>
                                    Respect and Professionalism
                                </li>
                            </ul>

                        </div>
                        <span class="font-medium">What Reviews Will Not Be Allowed:</span>
                        <ul class="list-disc pl-4">
                            <li>
                                Non-Personal Experience
                            </li>
                            <li>
                                Offensive Content
                            </li>
                            <li>
                                Defamatory Language
                            </li>
                            <li>
                                Allegations of Illegal Activity
                            </li>
                            <li>
                                Promotional Content
                            </li>
                            <li>
                                Conflicts of Interest
                            </li>
                            <li>
                                Irrelevant Information
                            </li>
                        </ul>
                    </div>


                    <div>
                        Your insightful, honest, and fair review is invaluable to our Landscape founder community.
                        Consider referencing our '<a
                        class="underline"
                        href="https://landscapeventures.notion.site/Landscape-s-Fair-Review-Policy-85f47740cb9b4b60b8c74e2e9fb36b95"
                        target="_blank">Review Do's and Don'ts</a>' guide for guidance.
                    </div>
                </div>

                <ValidationErrors/>

                <template v-if="currentStep === 1">
                    <div>
                        <div v-if="!customInvestorFormVisible">
                            <Label
                                for="company_name"
                                value="Name of Investor*"
                            />

                            <VueMultiselect
                                ref="multiselect"
                                v-model="selectedInvestor"
                                :internal-search="false"
                                :loading="investorSearchLoading"
                                :multiple="false"
                                :options="investors"
                                :required="true" :searchable="true"
                                :taggable="false"
                                class="mt-1"
                                placeholder="Search for Investors and Venture Capital Funds"
                                selected-label=""
                                @search-change="searchInvestors"
                            >

                                <template #singleLabel="props">
                                    <div class="flex">
                                        <div class="flex h-5 w-5 flex-none items-center justify-center rounded-lg">
                                            <img :src="props.option.image" class="w-5 h-5 object-contain">
                                        </div>
                                        <div class="ml-2 flex-auto max-w-full min-w-0 ">
                                            {{ props.option.name }}
                                        </div>
                                    </div>
                                </template>


                                <template #option="props"

                                >
                                    <div class="flex w-full">
                                        <div class="flex h-5 w-5 flex-none items-center justify-center rounded-lg">
                                            <img :src="props.option.image" class="w-5 h-5 object-contain">
                                        </div>
                                        <div class="ml-4 flex-auto max-w-full min-w-0 ">
                                            <div class="flex items-center text-sm font-medium">
                                                <div class="pr-1">
                                                    {{ props.option.name }}
                                                </div>

                                                <CheckBadgeIcon
                                                    v-if="props.option.has_owner"
                                                    class="w-5 h-5 text-zinc-800"/>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </VueMultiselect>

                            <InputError
                                :message="form.errors.investor"
                                class="mt-2"
                            />
                        </div>


                        <div>

                            <button
                                v-if="!customInvestorFormVisible" class="font-medium text-gray-400 text-sm py-2"
                                @click.prevent="showCustomInvestorForm">
                                Can't find the Investor? →
                            </button>


                            <div v-if="customInvestorFormVisible">
                                <Label
                                    for="company_name"
                                    value="Enter the Investor's name below*"
                                />

                                <TextInput v-model="form.investor_name" placeholder="Type the Investor's name"/>

                                <InputError
                                    :message="form.errors.investor_name"
                                    class="mt-2"
                                />
                            </div>

                            <button
                                v-if="customInvestorFormVisible" class="font-medium text-gray-400 text-sm py-2"
                                @click.prevent="hideCustomInvestorForm">
                                ← Or, try searching for the Investor again
                            </button>
                        </div>


                    </div>
                    <div class="survey-question">
                        <Label>
                            What was your relationship with {{ investorName }}?*
                        </Label>
                        <div class="text-gray-500 text-sm mb-1">
                            This helps us keep your review questions relevant. The more significant your relationship,
                            the
                            more factors you'll be able to score the investor on.
                        </div>
                        <div class="survey-question__control">
                            <select
                                v-model="form.relationship"
                                class="select-input"
                                name="relationship" required>
                                <option disabled value="">
                                    Please choose...
                                </option>
                                <option v-for="(option, key) in options.relationship" :key="key" :value="option">
                                    {{ option }}
                                </option>
                            </select>
                        </div>
                        <InputError
                            :message="form.errors.relationship"
                            class="mt-2"
                        />
                    </div>
                    <div class="survey-question">
                        <Label>
                            What stage was your startup when you dealt with {{ investorName }}?*
                        </Label>
                        <div class="survey-question__control">
                            <select
                                v-model="form.round"
                                class="select-input" name="round" required
                            >

                                <option disabled value="">
                                    Please choose...
                                </option>
                                <option v-for="(option, key) in options.stage" :key="key" :value="option">
                                    {{ option }}
                                </option>
                            </select>
                        </div>
                        <InputError
                            :message="form.errors.round"
                            class="mt-2"
                        />
                    </div>
                    <div class="survey-question">
                        <Label>
                            What timeframe (roughly) were you dealing with {{ investorName }}?*
                        </Label>
                        <div class="survey-question__control">
                            <select
                                v-model="form.timeframe"
                                class="select-input"
                                required>
                                <option disabled value="">
                                    Please choose...
                                </option>
                                <option v-for="(option, key) in options.timeframe" :key="key" :value="option">
                                    {{ option }}
                                </option>
                            </select>
                        </div>
                        <InputError
                            :message="form.errors.timeframe"
                            class="mt-2"
                        />
                    </div>
                    <div class="survey-question">
                        <Label> And were you a first time founder at the time?*</Label>

                        <div class="survey-question__control">
                            <select
                                v-model="form.first_time_founder"
                                class="select-input"
                                required>
                                <option disabled value="">
                                    Please choose...
                                </option>
                                <option :value="1">
                                    Yes
                                </option>

                                <option :value="0">
                                    No
                                </option>
                            </select>
                        </div>
                        <InputError
                            :message="form.errors.first_time_founder"
                            class="mt-2"
                        />
                    </div>
                    <div class="my-4 flex justify-end">
                        <ButtonPrimary type="button" @click.prevent="secondStep">
                            Continue
                        </ButtonPrimary>
                    </div>
                </template>


                <template v-if="currentStep === 2">
                    <div class="survey-question">
                        <Label> Ratings*</Label>

                        <div class="survey-question__control grid lg:grid-cols-2 grid-cols-1">
                            <ReviewFormRating
                                v-for="(rating, key) in availableRatings" :key="key" :errors="form.errors"
                                :help-text="rating.helpText" :high-score-label="rating.highScoreLabel"
                                :label="rating.label"
                                :low-score-label="rating.lowScoreLabel" :name="rating.name"
                                :options="rating.options" @selected="updateValue"/>
                        </div>
                    </div>

                    <div class="survey-question">
                        <Label>
                            How likely is it that you would recommend {{ investorName }} to a fellow founder?*
                        </Label>
                        <div class="survey-question__control">
                            <NumberRating
                                :errors="form.errors" class="mt-2" name="recommendation"
                                @selected="updateValue"/>
                        </div>
                    </div>

                    <div class="survey-question">
                        <Label>
                            Briefly summarise your experience with {{ investorName }}?*
                        </Label>
                        <div class="text-gray-500 text-sm">
                            The quantitative stuff is super useful, but the responses that founders tell us help them
                            most are
                            the Founders Voice comments.
                        </div>
                        <div class="survey-question__control">
                    <textarea
                        v-model="form.content"
                        class="textarea-input" name="review"
                        required/>
                        </div>
                    </div>

                    <div class="survey-question">
                        <Label>
                            What advice would you give {{ investorName }}?*
                        </Label>
                        <div class=" text-gray-500 text-sm">
                            Landscape Verified Partners will be able to see this response, so keep it clean, and keep it
                            helpful.

                            Consider this response to be your vote on how you think VC should be done better.
                        </div>
                        <div class="survey-question__control">
                    <textarea
                        v-model="form.feedback"
                        class="textarea-input" name="feedback"
                        required/>

                            <InputError
                                :message="form.errors.feedback"
                                class="mt-2"
                            />
                        </div>
                    </div>

                    <div class="my-4 flex justify-end space-x-4">
                        <ButtonDefault
                            class="h-10 px-8 py-3 bg-white rounded-[100px] border border-gray-200 justify-center items-center gap-2 inline-flex"
                            @click.prevent="firstStep">
                            Go back
                        </ButtonDefault>

                        <ButtonPrimary :disabled="form.processing" type="submit">
                            Submit Review
                        </ButtonPrimary>
                    </div>
                </template>
            </form>
        </div>
    </AppLayout>
</template>
<script>
import UnifiedLayoutTopbar from '@/Layouts/Legacy/UnifiedLayoutTopbar.vue';
import HeadingOne from '@/Components/Legacy/HeadingOne.vue';
import Label from '@/Components/Legacy/Jetstream/Label.vue';
import InputError from '@/Components/Legacy/Jetstream/InputError.vue';
import VueMultiselect from 'vue-multiselect';
import axios from 'axios';
import {CheckBadgeIcon} from '@heroicons/vue/24/solid/esm/index.js';
import ReviewFormRating from '@/Components/Legacy/ReviewFormRating.vue';
import NumberRating from '@/Components/Legacy/NumberRating.vue';
import ValidationErrors from '@/Components/Legacy/Jetstream/ValidationErrors.vue';
import LoginModal from '@/Components/Legacy/LoginModal.vue';
import {computed} from 'vue';
import {usePage} from '@inertiajs/vue3';

import lodash, {first} from 'lodash';
import Input from '@/Components/Legacy/Jetstream/Input.vue';
import AppLayout from '@/Layouts/Legacy/2023/AppLayout.vue';
import ButtonPrimary from '@/Components/Legacy/2023/ButtonPrimary.vue';
import ButtonDefault from '@/Components/Legacy/2023/ButtonDefault.vue';
import TextareaInput from '@/Components/Legacy/2023/Icons/TextareaInput.vue';
import SelectInput from '@/Components/Legacy/2023/Icons/SelectInput.vue';
import TextInput from '@/Components/Legacy/2023/Icons/TextInput.vue';

const {isEmpty} = lodash;

export default {
    components: {
        TextInput,
        SelectInput,
        TextareaInput,
        ButtonDefault,
        ButtonPrimary,
        AppLayout,
        Input,
        LoginModal,
        ValidationErrors,
        ReviewFormRating,
        NumberRating,
        HeadingOne,
        UnifiedLayoutTopbar,
        Label,
        InputError,
        VueMultiselect,
        CheckBadgeIcon,
    },
    props: {
        investor: {
            type: Object,
            required: false,
            default: () => {
            }
        }
    },
    setup() {
        const user = computed(() => usePage().props.user);
        return {user};
    },
    data() {
        return {
            currentStep: 1,
            loginModalVisible: false,
            form: this.$inertia.form({
                investor: null,
                investor_name: null,
                relationship: '',
                round: '',
                timeframe: '',
                first_time_founder: '',
                content: null,
                feedback: null,
                approachability: 0,
                approachability_note: '',
                beyond_money: 0,
                beyond_money_note: '',
                board_activity: 0,
                board_activity_note: '',
                close_time: 0,
                close_time_note: '',
                diversity: 0,
                diversity_note: '',
                due_diligence: 0,
                due_diligence_note: '',
                fees: 0,
                fees_note: '',
                terms: 0,
                terms_note: '',
                professionalism: 0,
                professionalism_note: '',
                punctuality: 0,
                punctuality_note: '',
                response_time: 0,
                response_time_note: '',
                support: 0,
                support_note: '',
                recommendation: 0,
                source: 'organic',
            }),
            selectedInvestor: null,
            customInvestorFormVisible: false,
            investorSearchLoading: false,
            investors: [],
            raisedOnly: [
                'diversity', 'close_time', 'terms', 'fees', 'board_activity', 'support', 'beyond_money', 'due_diligence'
            ],
            options: {
                ratings: [
                    {
                        label: 'Approachability',
                        name: 'approachability',
                        helpText: 'How easy was in to get in touch with them? Were there hoops to jump through?',
                        lowScoreLabel: 'Very Unapproachable',
                        highScoreLabel: 'Very Approachable',
                        options: [
                            'They require a warm intro',
                            'They don\'t have contact info listed publicly',
                            'They never responded to me',
                            'Other'
                        ]
                    },
                    {
                        label: 'Response Time',
                        name: 'response_time',
                        helpText: 'Generally speaking, how long does it take them to get back to you?',
                        lowScoreLabel: 'Snail Pace 🐌',
                        highScoreLabel: 'Rapid ⚡',
                        options: [
                            'They never replied to me',
                            'It took ages to respond to my emails',
                            'Meetings were scheduled too far in the future',
                            'Reviewing my deck took ages',
                            'Getting a no took ages',
                            'Other'
                        ]
                    },
                    {
                        label: 'Professionalism',
                        name: 'professionalism',
                        helpText: 'All things considered, how would you rate the professionalism of this investor?',
                        lowScoreLabel: 'Very Unprofessional',
                        highScoreLabel: 'Very Professional',
                        options: [
                            'Acted inappropriately',
                            'Made me feel uncomfortable',
                            'Weren\'t paying attention during my pitch',
                            'Were rude',
                            'They never responded to me',
                            'Other'
                        ]
                    },
                    {
                        label: 'Punctuality',
                        name: 'punctuality',
                        helpText: 'Were they late for meetings? Did they cancel last minute?',
                        lowScoreLabel: 'Very Poor Punctuality',
                        highScoreLabel: 'Excellent Punctuality',
                        options: [
                            'Late for meetings',
                            'Cancelled meetings',
                            'Last minute changes',
                            'Other'
                        ]
                    },
                    {
                        label: 'Diversity',
                        name: 'diversity',
                        helpText: 'How diverse is their team? Are they making investments into diverse founding teams?',
                        lowScoreLabel: 'Poor Diversity',
                        highScoreLabel: 'Excellent Diversity',
                        options: [
                            'The VC\'s team lacks diversity',
                            'The VC\'s portfolio lacks diversity',
                            'Other'
                        ]
                    },
                    {
                        label: 'Close Time',
                        name: 'close_time',
                        helpText: 'How would you rank the length of time it took to close capital?',
                        lowScoreLabel: 'Very Long',
                        highScoreLabel: 'Very Short',
                        options: [
                            'Getting a term sheet took ages',
                            'Negotiation took ages',
                            'Due Diligence took ages',
                            'Other'
                        ]
                    },
                    {
                        label: 'Due Diligence',
                        name: 'due_diligence',
                        helpText: 'What was the VC\'s due diligence process like?',
                        lowScoreLabel: 'Complete hell',
                        highScoreLabel: 'A walk in the park',
                        options: [
                            'They inspected literally everything',
                            'They spent a long time on our finances',
                            'The tech review took ages',
                            'It was stressful',
                            'They didn\'t move fast',
                            'There was a constant air of uncertainty',
                            'Other'
                        ]
                    },
                    {
                        label: 'Terms',
                        name: 'terms',
                        helpText: 'How founder friendly were the terms of the deal?',
                        lowScoreLabel: 'Very Founder Unfriendly',
                        highScoreLabel: 'Very Founder Friendly',
                        options: [
                            'They took preferential shares',
                            'They lowered our valuation after due diligence',
                            'They demanded a new vesting schedule',
                            'They beat down our valuation',
                            'Their terms were unnecessarily complicated',
                            'Other'
                        ]
                    },
                    {
                        label: 'Fees',
                        name: 'fees',
                        helpText: 'Did they charge completion fees, monthly monitoring fees, or fees of any description? How expensive were they?',
                        lowScoreLabel: 'Sky High Fees',
                        highScoreLabel: 'No Fees',
                        options: [
                            'We had to cover their legal fees',
                            'They charge monthly monitoring fees',
                            'They charged a completion fee',
                            'They charge expenses for board meetings',
                            'Other'
                        ]
                    },
                    {
                        label: 'Board Activity',
                        name: 'board_activity',
                        helpText: 'Did they take a board seat? If so, did they actively add or detract value from your company during their time on the board?',
                        lowScoreLabel: 'Very Low Value Board Member',
                        highScoreLabel: 'Very High Value Board Member',
                        optional: true,
                        options: [
                            'They never attend board meetings',
                            'They made board meetings unenjoyable',
                            'They made board meetings unproductive',
                            'Other'
                        ]
                    },
                    {
                        label: 'Supportiveness',
                        name: 'support',
                        helpText: 'How supportive are they in general of you and your startup? Do they trust your judgement and support you in your execution, or try and micro-manage?',
                        lowScoreLabel: 'Very Unsupportive',
                        highScoreLabel: 'Very Supportive',
                        options: [
                            'They pushed us to change our model',
                            'They created unrealistic deadlines and targets',
                            'They made board meetings unpleasant',
                            'They created more stress than benefits',
                            'Other'
                        ]
                    },
                    {
                        label: 'Beyond Money',
                        name: 'beyond_money',
                        helpText: 'Every investor prides themselves on their ability to add value beyond just money. How successful were they at this?',
                        lowScoreLabel: 'No value beyond capital',
                        highScoreLabel: 'Huge value beyond capital',
                        options: [
                            'They tried, but added no value beyond capital',
                            'They didn\'t try to add any value beyond capital',
                            'Other'
                        ]
                    }
                ],
                relationship: [
                    "I've raised capital from them",
                    'I tried raising capital from them',
                    "I've interacted with them"
                ],
                stage: [
                    'Pre-Idea',
                    'Idea',
                    'Pre-seed',
                    'Seed',
                    'Series A',
                    'Series B',
                    'Series C',
                    'Series D',
                    'IPO'
                ],
                timeframe: [
                    'Within the last month',
                    'Within the last 3 months',
                    'Within the last 6 months',
                    'Within the last year',
                    'Within the last 3 years',
                    'Within the last 5 years',
                    'Within the last 10 years',
                    '10 years+'
                ]
            },
        };
    },
    computed: {
        investorName() {
            if (this.selectedInvestor) {
                return this.selectedInvestor.name;
            }

            return 'this investor';
        },
        availableRatings() {
            if (this.form.relationship === 'I\'ve raised capital from them') {
                return this.options.ratings;
            }
            return this.options.ratings.filter((item) => {
                return this.raisedOnly.indexOf(item.name) === -1;
            });
        }
    },

    watch: {
        selectedInvestor(val) {
            if (typeof val !== 'object' || val === null || !val.hasOwnProperty('slug')) {
                this.form.investor = null;
                return;
            }
            this.form.investor = val.slug ?? null;
        }
    },
    mounted() {
        if (isEmpty(this.investor)) {
            return;
        }

        this.selectedInvestor = this.investor.data;
    },

    methods: {
        first,
        showCustomInvestorForm() {
            this.selectedInvestor = null;
            this.customInvestorFormVisible = true;
        },

        hideCustomInvestorForm() {
            this.form.investor_name = null;
            this.customInvestorFormVisible = false;
        },
        submit() {

            // Check if user is logged in...
            if (!this.user) {
                // User isn't logged in, show login modal...
                this.loginModalVisible = true;
                return;
            }

            if (!this.$refs.form.checkValidity()) {
                // Try focus on the error
                this.$refs.form.reportValidity();
                return;
            }

            this.form.post(this.route('reviews.store'));
        },
        toggleLoginModal(val) {
            this.loginModalVisible = val;
        },
        loginFormSuccess() {
            this.loginModalVisible = false;
            this.submit();
        },
        updateValue(item) {
            this.form[item.name] = item.value;
        },
        secondStep() {
            this.currentStep = 2;
        },
        firstStep() {
            this.currentStep = 1;
        },
        searchInvestors(query) {
            this.investorSearchLoading = true;

            axios.get(this.route('api.topbar-search'), {params: {query: query, type: 'investor'}}).then((response) => {
                this.investors = response.data.data;
                this.investorSearchLoading = false;
            }).catch((error) => {
                this.investorSearchLoading = false;
            });
        }
    }
};
</script>
